import React, { useEffect, useState } from 'react';
import { analyticsService } from '../services/analyticsService';

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState<any | null>(null);
  const [daily, setDaily] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Daily complete analytics
  const [useRange, setUseRange] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [dailyCompleteData, setDailyCompleteData] = useState<any | null>(null);
  const [dailyCompleteLoading, setDailyCompleteLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Filters
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');


  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const o = await analyticsService.getOverview(7);
        const d = await analyticsService.getDaily(7);
        if (!mounted) return;
        setOverview(o);
        setDaily(d || []);
      } catch (e) {
        console.error('Failed to load analytics', e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const loadDailyComplete = async (page?: number) => {
    setDailyCompleteLoading(true);
    try {
      const options: any = {
        page: page || currentPage,
        per_page: perPage
      };

      if (useRange) {
        options.start_date = startDate;
        options.end_date = endDate;
      } else {
        options.date = selectedDate;
      }

      if (selectedModel) {
        options.model = selectedModel;
      }

      if (selectedStatus) {
        options.status = parseInt(selectedStatus);
      }

      const data = await analyticsService.getDailyComplete(options);
      setDailyCompleteData(data);
      if (page) {
        setCurrentPage(page);
      }
    } catch (e) {
      console.error('Failed to load daily complete data', e);
      setDailyCompleteData(null);
    } finally {
      setDailyCompleteLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedModel('');
    setSelectedStatus('');
    setCurrentPage(1);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Analytics Dashboard</h1>
      {loading && <div className="text-sm text-gray-400">Loading...</div>}

      {overview && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-white rounded shadow">
            <div className="text-xs text-gray-500">Total requests</div>
            <div className="text-xl font-bold">{overview.total_requests}</div>
          </div>
          <div className="p-4 bg-white rounded shadow">
            <div className="text-xs text-gray-500">Successful</div>
            <div className="text-xl font-bold">{overview.successful_requests}</div>
          </div>
          <div className="p-4 bg-white rounded shadow">
            <div className="text-xs text-gray-500">Failed</div>
            <div className="text-xl font-bold">{overview.failed_requests}</div>
          </div>
          <div className="p-4 bg-white rounded shadow">
            <div className="text-xs text-gray-500">Avg response (ms)</div>
            <div className="text-xl font-bold">{Math.round(overview.average_response_time_ms)}</div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Daily usage (last 7 days)</h2>
        <div className="bg-white rounded shadow p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th>Date</th>
                <th>Requests</th>
                <th>Tokens</th>
                <th>Cost (USD)</th>
                <th>Success %</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => (
                <tr key={d.date} className="border-t">
                  <td className="py-2">{d.date}</td>
                  <td>{d.requests}</td>
                  <td>{d.tokens}</td>
                  <td>{d.cost_usd}</td>
                  <td>{d.success_rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Complete Analytics panel */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Daily Complete Analytics</h2>
        <div className="bg-white rounded shadow p-4">
          {/* Date Selection Controls */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <div className="flex items-center space-x-4 mb-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={!useRange}
                  onChange={() => setUseRange(false)}
                  className="mr-2"
                />
                Single Date
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={useRange}
                  onChange={() => setUseRange(true)}
                  className="mr-2"
                />
                Date Range
              </label>
            </div>

            {!useRange ? (
              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-600">Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border px-2 py-1 rounded"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-600">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border px-2 py-1 rounded"
                />
                <label className="text-sm text-gray-600">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border px-2 py-1 rounded"
                />
              </div>
            )}
          </div>

          {/* Filters and Pagination Controls */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Model Filter:</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full border px-2 py-1 rounded"
                >
                  <option value="">All Models</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                  <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                  <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Status Filter:</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full border px-2 py-1 rounded"
                >
                  <option value="">All Status</option>
                  <option value="200">Success (200)</option>
                  <option value="400">Bad Request (400)</option>
                  <option value="401">Unauthorized (401)</option>
                  <option value="429">Rate Limited (429)</option>
                  <option value="500">Server Error (500)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Per Page:</label>
                <select
                  value={perPage}
                  onChange={(e) => setPerPage(parseInt(e.target.value))}
                  className="w-full border px-2 py-1 rounded"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div className="flex items-end space-x-2">
                <button
                  onClick={resetFilters}
                  className="px-3 py-1 bg-gray-500 text-white rounded text-sm"
                >
                  Reset
                </button>
                <button
                  onClick={() => loadDailyComplete()}
                  disabled={dailyCompleteLoading}
                  className="px-4 py-1 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                >
                  {dailyCompleteLoading ? 'Loading...' : 'Load'}
                </button>
              </div>
            </div>
          </div>


          {dailyCompleteLoading && <div className="text-sm text-gray-500">Loading daily complete data...</div>}

          {dailyCompleteData ? (
            <div>
              {/* Summary Section */}
              <div className="mb-6">
                <h3 className="text-md font-semibold mb-3">Summary for {dailyCompleteData.summary?.date}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Total Requests</div>
                    <div className="text-lg font-bold">{dailyCompleteData.summary?.total_requests}</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Success Rate</div>
                    <div className="text-lg font-bold">{dailyCompleteData.summary?.success_rate}%</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Total Cost</div>
                    <div className="text-lg font-bold">${dailyCompleteData.summary?.total_cost_usd?.toFixed(6)}</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Avg Response Time</div>
                    <div className="text-lg font-bold">{dailyCompleteData.summary?.average_response_time_ms?.toFixed(0)}ms</div>
                  </div>
                </div>

                {/* Models Breakdown */}
                {dailyCompleteData.summary?.models_breakdown && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold mb-2">Models Breakdown</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500">
                            <th>Model</th>
                            <th>Requests</th>
                            <th>Prompt Tokens</th>
                            <th>Completion Tokens</th>
                            <th>Total Tokens</th>
                            <th>Cost (USD)</th>
                            <th>Avg Cost/Token</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyCompleteData.summary.models_breakdown.map((model: any, idx: number) => (
                            <tr key={idx} className="border-t">
                              <td className="py-2 font-medium">{model.model}</td>
                              <td>{model.requests}</td>
                              <td>{model.prompt_tokens}</td>
                              <td>{model.completion_tokens}</td>
                              <td>{model.total_tokens}</td>
                              <td>${model.total_cost_usd?.toFixed(6)}</td>
                              <td>{model.average_cost_per_token?.toExponential(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Requests Section */}
              {dailyCompleteData.recent_requests && (
                <div>
                  <h3 className="text-md font-semibold mb-3">Recent Requests</h3>
                  <div className="overflow-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th>ID</th>
                          <th>Created</th>
                          <th>Endpoint</th>
                          <th>Method</th>
                          <th>Status</th>
                          <th>Response Time</th>
                          <th>Model</th>
                          <th>Tokens</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyCompleteData.recent_requests.map((req: any) => (
                          <tr key={req.id} className="border-t">
                            <td className="py-2 font-mono text-xs">{req.id}</td>
                            <td>{new Date(req.created).toLocaleTimeString()}</td>
                            <td className="truncate max-w-xs">{req.endpoint}</td>
                            <td>{req.method}</td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs ${
                                req.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {req.status}
                              </span>
                            </td>
                            <td>{req.response_time_ms?.toFixed(0)}ms</td>
                            <td>{req.model_used || '-'}</td>
                            <td>{req.total_tokens || '-'}</td>
                            <td>{req.total_cost_usd ? `$${req.total_cost_usd.toFixed(6)}` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {dailyCompleteData.pagination && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing page {dailyCompleteData.pagination.page} of {dailyCompleteData.pagination.total_pages}
                        ({dailyCompleteData.pagination.total_requests} total requests)
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => loadDailyComplete(1)}
                          disabled={dailyCompleteData.pagination.page === 1 || dailyCompleteLoading}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                        >
                          First
                        </button>
                        <button
                          onClick={() => loadDailyComplete(dailyCompleteData.pagination.page - 1)}
                          disabled={dailyCompleteData.pagination.page === 1 || dailyCompleteLoading}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded">
                          {dailyCompleteData.pagination.page}
                        </span>
                        <button
                          onClick={() => loadDailyComplete(dailyCompleteData.pagination.page + 1)}
                          disabled={dailyCompleteData.pagination.page === dailyCompleteData.pagination.total_pages || dailyCompleteLoading}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => loadDailyComplete(dailyCompleteData.pagination.total_pages)}
                          disabled={dailyCompleteData.pagination.page === dailyCompleteData.pagination.total_pages || dailyCompleteLoading}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No daily complete data loaded.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
